import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface ChannelAllowlistEditorProps {
  rows: string[];
  onChange: (rows: string[]) => void;
}

/**
 * Dynamic editor for a slack connector's channel allowlist (spec 0011): a
 * plain list of Slack channel ids. An empty list means every channel is
 * allowed (DMs always pass). Mirrors the repeatable-row editor pattern used
 * elsewhere (HeaderEditor, the email allowlist).
 */
export function ChannelAllowlistEditor({ rows, onChange }: ChannelAllowlistEditorProps) {
  return (
    <div className="space-y-3" data-testid="slack-channel-allowlist">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Channel allowlist</label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...rows, ''])}
          data-testid="add-channel-button"
        >
          Add channel
        </Button>
      </div>
      <p className="text-xs text-gray-500" data-testid="slack-channel-hint">
        Slack channel ids (for example C0123456789). An empty list lets the agent respond in
        every channel it is added to; direct messages always pass.
      </p>
      {rows.length === 0 && <p className="text-xs text-gray-500">No channels: every channel is allowed.</p>}
      {rows.map((channel, index) => (
        <div key={index} className="flex items-end gap-2" data-testid="channel-row">
          <div className="flex-1">
            <Input
              label={`Channel ${index + 1}`}
              value={channel}
              onChange={(e) => onChange(rows.map((c, i) => (i === index ? e.target.value : c)))}
              placeholder="C0123456789"
              data-testid="channel-input"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
            data-testid="remove-channel-button"
          >
            Remove
          </Button>
        </div>
      ))}
    </div>
  );
}
